import {
    Box, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
    FormControl, FormLabel, Button, FormErrorMessage, useDisclosure
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import IdentityDetailLogo from "../../../../../assets/images/IdentityDetailLogo.svg";
import EditLogo from "../../../../../assets/images/EditLogo.svg";
import { EmployeerDetail } from "./moda";
import { useForm } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { CustomToast } from "../../../../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { HeirarchyDetal } from "./moda";
import Typeahead from "../../../../../components/AutoCompleteInput/Typeahead";
import { useAuth } from "../../../../../hooks/useAuth";

interface Props {
    employeeId: number | null;
    personalDetail?: EmployeerDetail;
    reload: () => void;
}

export interface IHierarchy {
    empId?: number | null;
    managerId: number;
    teamLeadId: number;
    branchHead?: string;
    manager?: string;
    teamLead?: string;
    managerObject?: { employeeID: number, emp_Name: string, emp_Label?: string }
    teamLeadObject?: { employeeID: number, emp_Name: string, emp_Label?: string }
    branchHeadObject?: { employeeID: number; emp_Name: string; emp_Label?: string };
};

const withCode = (list: any[]) => list.map((e) => ({ ...e, emp_Label: e.emp_Code ? `${e.emp_Name} (${e.emp_Code})` : e.emp_Name, }));

const Hierarchy = ({ employeeId, personalDetail, reload }: Props) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const [teamLeadOptions, setTeamLeadOptions] = useState<any[]>([]);
    const [managerOptions, setManagerOptions] = useState<any[]>([]);
    const teamLeadTimeout = useRef<any>(null);
    const managerTimeout = useRef<any>(null);
    const { hasPermission } = useAuth();
    const HierarchySchema: any = yup.object().shape({
        managerObject: yup.object().shape({
            employeeID: yup.number().typeError("Manager is required").required("Manager is required").moreThan(0, "Manager is required"),
            emp_Name: yup.string().nullable()
        }),
        // teamLeadObject: yup.object().shape({
        //     employeeID: yup.number().required("Team Lead is required").min(1, "Team Lead is required"),
        //     emp_Name: yup.string().nullable()
        // }),
        teamLeadObject: yup.object().shape({
            employeeID: yup.number().typeError("Manager is required").required("Team Lead is required").min(1, "Team Lead is required"),
            emp_Name: yup.string().nullable()
        }),
    });
    const { control, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<IHierarchy>({
        resolver: yupResolver(HierarchySchema),
        defaultValues: {},
    });
    function handleEditClick() {
        const label = (name?: string, code?: string) =>
            code ? `${name ?? ''} (${code})` : name ?? '';
        reset({
            managerObject: {
                employeeID: personalDetail?.managerId ?? 0,
                emp_Name: personalDetail?.manager ?? '',
                emp_Label: label(personalDetail?.manager, personalDetail?.managerCode),
            },
            teamLeadObject: {
                employeeID: personalDetail?.teamLeadId ?? 0,
                emp_Name: personalDetail?.teamLead ?? '',
                emp_Label: label(personalDetail?.teamLead, personalDetail?.teamLeadCode),
            },
            branchHeadObject: {
                employeeID: personalDetail?.branchHeadId ?? 0,
                emp_Name: personalDetail?.branchHead ?? '',
                emp_Label: label(personalDetail?.branchHead, personalDetail?.branchHeadCode),
            },
        });
        onOpen();
    }

    /** ✅ Team lead handler (already debounced with timeout) */
    const handleTeamLeadChange = (value?: string) => {
        if (teamLeadTimeout.current) clearTimeout(teamLeadTimeout.current);
        teamLeadTimeout.current = setTimeout(async () => {
            if (value === undefined) setTeamLeadOptions([]);
            else if (value.length > 0) {
                const res = await fetchApi("Hrlense_Employee/EmployeeList", "GET", null, `filter=${value}&assignedCompany=${personalDetail?.company_Id}`, "");
                if (res) {
                    setTeamLeadOptions(withCode(res));
                }
            }
        }, 500);
    };

    const handleManagerChange = (value?: string) => {
        if (managerTimeout.current) clearTimeout(managerTimeout.current);
        managerTimeout.current = setTimeout(async () => {
            if (value === undefined) setManagerOptions([]);
            else if (value.length > 0) {
                const res = await fetchApi("Hrlense_Employee/EmployeeList", "GET", null, `filter=${value}&assignedCompany=${personalDetail?.company_Id}`, "");
                if (res) {
                    setManagerOptions(withCode(res));
                }
            }
        }, 500);
    };

    /** ✅ Save */
    const onSubmit = async (data: IHierarchy) => {
        data.empId = employeeId;
        data.managerId = data.managerObject?.employeeID ?? 0;
        data.teamLeadId = data.teamLeadObject?.employeeID ?? 0;
        let endpoint = `Hrlense_Employee/personalDetail/${employeeId}`;
        const response = await fetchApi(endpoint, "PUT", data, null, "Hierarchy Details Updated");
        if (response) {
            reload();
            onClose();
        }
    };

    /** ✅ Prefill */
    useEffect(() => {
        if (personalDetail) {
            reset({
                managerId: personalDetail?.managerId,
                manager: personalDetail?.manager || "",
            });
        }
    }, [personalDetail, reset]);

    return (
        <>
            <Box pt={2} mb={6} className="main">
                <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
                    <Flex align="center" gap={3}>
                        <Image src={IdentityDetailLogo} alt="Benifits & ID Numberss" />
                        <Text className="font-poppins text_sm text_medium col">Hierarchy Details</Text>
                    </Flex>
                    {hasPermission('Employee', 'UpdateHierarchyDetail') && (
                        <IconButton
                            aria-label="Edit"
                            icon={<Image src={EditLogo} />}
                            variant="none"
                            onClick={() => handleEditClick()}
                        />)}
                </Flex>

                <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
                    {HeirarchyDetal.map((data, index) => {
                        const value = personalDetail?.[data?.key];
                        const codeMap: Record<string, string | undefined> = {
                            manager: personalDetail?.managerCode,
                            teamLead: personalDetail?.teamLeadCode,
                            branchHead: personalDetail?.branchHeadCode,
                        };
                        const code = codeMap[data?.key as string];

                        return (
                            <Grid key={index} templateColumns="1fr 1fr" alignItems="center" mb={2}>
                                <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)">
                                    {data?.label}
                                </Text>
                                <Text className="font-poppins text_sm text_medium col">
                                    {value ? (code ? `${value} (${code})` : value) : ""}
                                </Text>
                            </Grid>
                        );
                    })}
                </Box>
            </Box>

            {/* Drawer for editing */}
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>Update Hierarchy Details</DrawerHeader>
                    <DrawerBody>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* Manager */}
                            <FormControl isInvalid={!!errors.managerObject?.employeeID} mb={4}>
                                <FormLabel>Manager</FormLabel>
                                <Typeahead name="managerObject" control={control} placeholder="Select manager..." loading={loading}
                                    onCompletion={(query) => handleManagerChange(query)} options={managerOptions}
                                    optionLabel="emp_Label" optionValue="employeeID"
                                    val={{
                                        employeeID: personalDetail?.managerId,
                                        emp_Label: personalDetail?.managerCode
                                            ? `${personalDetail?.manager} (${personalDetail?.managerCode})`
                                            : personalDetail?.manager,
                                    }}
                                />

                                <small className="text-danger">{errors.managerObject?.employeeID?.message}</small>
                                <FormErrorMessage>{errors.managerId?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={!!errors.teamLeadObject?.employeeID} mb={4}>
                                <FormLabel>Team Lead</FormLabel>
                                <Typeahead name="teamLeadObject" control={control} placeholder="Select Team Lead..." loading={loading}
                                    onCompletion={(query) => handleTeamLeadChange(query)} options={teamLeadOptions}
                                    optionLabel="emp_Label" optionValue="employeeID"
                                    val={{
                                        employeeID: personalDetail?.teamLeadId,
                                        emp_Label: personalDetail?.teamLeadCode
                                            ? `${personalDetail?.teamLead} (${personalDetail?.teamLeadCode})`
                                            : personalDetail?.teamLead,
                                    }}
                                />
                                <FormErrorMessage>{errors.teamLeadId?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl mb={4}>
                                <FormLabel>Branch Head</FormLabel>

                                <Typeahead
                                    name="branchHeadObject"
                                    control={control}
                                    placeholder="Branch Head"
                                    loading={false}
                                    options={[]}
                                    onCompletion={() => { }}
                                    optionLabel="emp_Label"
                                    optionValue="employeeID"
                                    val={{
                                        employeeID: personalDetail?.branchHeadId,
                                        emp_Label: personalDetail?.branchHeadCode
                                            ? `${personalDetail?.branchHead} (${personalDetail?.branchHeadCode})`
                                            : personalDetail?.branchHead,
                                    }}
                                    disabled={true}
                                />
                            </FormControl>

                            <Box mt={6} textAlign="right">
                                <Button type="submit" className="btn_theme btn_theme:hover" isLoading={isSubmitting}>
                                    Update
                                </Button>
                            </Box>
                        </form>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
};

export default Hierarchy;
